/**
 * Influencer page — Shopify Forms bridge
 * --------------------------------------
 * Forwards submissions from our custom-styled forms into the Shopify Forms
 * (app) form with ID 978681 which is rendered hidden on the page.
 *
 * Behaviour:
 *   1. On submit of a custom form, search the DOM for the Shopify Form
 *      (tries ~20 selectors, enters shadow roots, waits up to FORM_WAIT_MS).
 *   2. If found → copy values into its fields, click its submit button.
 *   3. If NOT found → fall back to the native `/contact` endpoint so the
 *      data is always captured (Admin → Contact form submissions).
 *
 * Debug:
 *   Set DEBUG = true (default while stabilising) to dump environment info
 *   into the console on every submit.
 */
(function () {
  'use strict';

  var SHOPIFY_FORM_ID = '978681';
  // Shopify Forms app renders the actual embed inside a wrapper with
  //   data-forms-id="forms-root-{ID}"
  // and a custom element <shopify-forms-embed>. The form itself is rendered
  // asynchronously (sometimes inside the embed's shadow root).
  var SHOPIFY_FORMS_ROOT_ID = 'forms-root-' + SHOPIFY_FORM_ID;
  var FORM_WAIT_MS = 5000;
  var FALLBACK_TO_CONTACT_ENDPOINT = true;
  var DEBUG = false;

  function log() {
    if (!DEBUG) return;
    try { console.log.apply(console, ['[influencer-forms]'].concat([].slice.call(arguments))); }
    catch (_) {}
  }
  function warn() {
    if (!DEBUG) return;
    try { console.warn.apply(console, ['[influencer-forms]'].concat([].slice.call(arguments))); }
    catch (_) {}
  }

  var SHOPIFY_FORM_SELECTORS = [
    // --- Real Shopify Forms app output (inline embed) ---
    '[data-forms-id="' + SHOPIFY_FORMS_ROOT_ID + '"]',
    '[data-form-root="true"][data-forms-id="' + SHOPIFY_FORMS_ROOT_ID + '"]',
    '[data-forms-id*="' + SHOPIFY_FORM_ID + '"]',
    '[data-form-root="true"]',
    'shopify-forms-embed',
    'shopify-forms-embed#app-embed',
    // --- Legacy / alternative markup ---
    '[data-shopify-forms-id="' + SHOPIFY_FORM_ID + '"]',
    '[data-form-id="' + SHOPIFY_FORM_ID + '"]',
    'shopify-forms-app[data-form-id="' + SHOPIFY_FORM_ID + '"]',
    'shopify-forms-app',
    'shopify-forms-form',
    'shopify-forms',
    'shopify-form',
    // --- Class / id based fallbacks ---
    '.shopify-forms-app form',
    '.shopify-form form',
    '[class*="shopify-forms"] form',
    '[class*="shopify-form"] form',
    '[id*="shopify-forms-" i] form',
    '[id*="ShopifyForm"] form',
    // --- Endpoint-based ---
    'form[action*="/apps/forms"]',
    'form[action*="/apps/shopify_forms"]',
    'form[action*="shopify-forms"]'
  ];

  /** Deep querySelector that descends into open shadow roots. */
  function qsDeep(selector, root) {
    root = root || document;
    var found;
    try { found = root.querySelector(selector); }
    catch (e) { return null; }
    if (found) return found;
    var nodes = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].shadowRoot) {
        var nested = qsDeep(selector, nodes[i].shadowRoot);
        if (nested) return nested;
      }
    }
    return null;
  }

  /** Given a host element, recursively look for the inner <form>, including
   *  descending into any open shadow roots. */
  function findInnerForm(host) {
    if (!host) return null;
    if (host.tagName === 'FORM') return host;
    // Plain descendant
    if (host.querySelector) {
      var direct = host.querySelector('form');
      if (direct) return direct;
    }
    // Shadow root of the host itself
    if (host.shadowRoot) {
      var shadowed = qsDeep('form', host.shadowRoot);
      if (shadowed) return shadowed;
    }
    // Any nested shadow root inside children
    var all = host.querySelectorAll ? host.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) {
        var nested = qsDeep('form', all[i].shadowRoot);
        if (nested) return nested;
      }
    }
    return null;
  }

  function findShopifyForm() {
    for (var i = 0; i < SHOPIFY_FORM_SELECTORS.length; i++) {
      var el = qsDeep(SHOPIFY_FORM_SELECTORS[i]);
      if (!el) continue;
      if (el.tagName === 'FORM') {
        log('Found Shopify Form via:', SHOPIFY_FORM_SELECTORS[i], el);
        return el;
      }
      var inner = findInnerForm(el);
      if (inner) {
        log('Found Shopify Form (nested) via:', SHOPIFY_FORM_SELECTORS[i], inner);
        return inner;
      }
      log('Found host but no inner form yet:', SHOPIFY_FORM_SELECTORS[i], el);
    }
    return null;
  }

  /** Returns a Promise that resolves with the form (or null after timeout). */
  function waitForShopifyForm(timeoutMs) {
    return new Promise(function (resolve) {
      var existing = findShopifyForm();
      if (existing) return resolve(existing);

      var observer = new MutationObserver(function () {
        var f = findShopifyForm();
        if (f) {
          observer.disconnect();
          clearTimeout(timer);
          resolve(f);
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      var timer = setTimeout(function () {
        observer.disconnect();
        resolve(null);
      }, timeoutMs);
    });
  }

  function dumpEnvironment() {
    if (!DEBUG) return;
    try {
      console.group('[influencer-forms] Environment dump');
      console.log('All <form> elements:', document.querySelectorAll('form'));
      console.log('data-forms-id (real Shopify Forms):', document.querySelectorAll('[data-forms-id]'));
      console.log('data-form-root:', document.querySelectorAll('[data-form-root]'));
      console.log('data-shopify-forms-id:', document.querySelectorAll('[data-shopify-forms-id]'));
      console.log('data-form-id:', document.querySelectorAll('[data-form-id]'));
      console.log('shopify-forms-embed:', document.querySelectorAll('shopify-forms-embed'));
      console.log('[id*="shopify" i]:', document.querySelectorAll('[id*="shopify" i]'));
      console.log('[class*="shopify" i]:', document.querySelectorAll('[class*="shopify" i]'));
      console.log('Web components:', document.querySelectorAll(
        'shopify-forms-app, shopify-forms, shopify-forms-form, shopify-forms-embed, shopify-form'
      ));
      console.groupEnd();
    } catch (_) {}
  }

  /** Keyword variations the Shopify Forms inputs might use for each field. */
  var FIELD_KEYWORDS = {
    name:      ['name', 'first_name', 'first-name', 'firstname', 'last_name', 'last-name', 'lastname', 'fullname', 'full_name', 'full-name', 'yourname', 'your-name'],
    email:     ['email', 'e-mail', 'e_mail', 'mail'],
    instagram: ['instagram', 'insta', '_ig_', 'ig-handle', 'ig_handle', '-ig-', 'iguser'],
    tiktok:    ['tiktok', 'tik-tok', 'tik_tok', '_tt_', 'tthandle', 'tt-handle', 'tiktokhandle']
  };

  function listVisibleInputs(shopifyForm) {
    return [].slice.call(
      shopifyForm.querySelectorAll('input, textarea')
    ).filter(function (el) {
      var t = (el.getAttribute('type') || '').toLowerCase();
      return t !== 'hidden' && t !== 'submit' && t !== 'button' && t !== 'checkbox' && t !== 'radio';
    });
  }

  function inputHaystack(input) {
    // Concatenate every attribute we might match on, lowercased.
    return [
      input.name,
      input.id,
      input.placeholder,
      input.getAttribute && input.getAttribute('aria-label'),
      input.getAttribute && input.getAttribute('data-field'),
      input.getAttribute && input.getAttribute('data-key'),
      input.getAttribute && input.getAttribute('autocomplete')
    ].filter(Boolean).join(' ').toLowerCase();
  }

  function findFieldInShopifyForm(shopifyForm, labelKey, inputs) {
    var lc = String(labelKey).toLowerCase();
    var keywords = FIELD_KEYWORDS[lc] || [lc];
    inputs = inputs || listVisibleInputs(shopifyForm);

    // 1) Match against attributes (name/id/placeholder/aria-label/data-*/autocomplete)
    for (var i = 0; i < inputs.length; i++) {
      var hay = inputHaystack(inputs[i]);
      for (var j = 0; j < keywords.length; j++) {
        if (hay.indexOf(keywords[j]) !== -1) return inputs[i];
      }
    }

    // 2) Associated <label> text
    for (var k = 0; k < inputs.length; k++) {
      var input = inputs[k];
      if (input.id) {
        var lbl = shopifyForm.querySelector('label[for="' + input.id + '"]');
        if (lbl) {
          var txt = (lbl.textContent || '').toLowerCase();
          for (var m = 0; m < keywords.length; m++) {
            if (txt.indexOf(keywords[m]) !== -1) return input;
          }
        }
      }
    }

    // 3) Closest <label> ancestor (for labels that wrap inputs)
    for (var n = 0; n < inputs.length; n++) {
      var closestLabel = inputs[n].closest && inputs[n].closest('label');
      if (closestLabel) {
        var ctxt = (closestLabel.textContent || '').toLowerCase();
        for (var p = 0; p < keywords.length; p++) {
          if (ctxt.indexOf(keywords[p]) !== -1) return inputs[n];
        }
      }
    }

    // 4) Type-based fallback for email
    if (lc === 'email') {
      var eInput = shopifyForm.querySelector('input[type="email"]');
      if (eInput) return eInput;
    }

    // 5) Positional fallback: if 4 visible inputs in the expected order
    //    (name, email, instagram, tiktok), map by index.
    var POSITION = { name: 0, email: 1, instagram: 2, tiktok: 3 };
    if (inputs.length === 4 && lc in POSITION) {
      return inputs[POSITION[lc]];
    }

    return null;
  }

  function inspectShopifyFormInputs(shopifyForm) {
    var inputs = listVisibleInputs(shopifyForm);
    return inputs.map(function (el) {
      return {
        tag: el.tagName,
        type: el.getAttribute('type') || '',
        name: el.name || '',
        id: el.id || '',
        placeholder: el.placeholder || '',
        ariaLabel: el.getAttribute('aria-label') || '',
        autocomplete: el.getAttribute('autocomplete') || ''
      };
    });
  }

  function setFieldValue(input, value) {
    if (!input) return false;
    var proto = input.tagName === 'TEXTAREA'
      ? window.HTMLTextAreaElement && window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement && window.HTMLInputElement.prototype;
    if (proto) {
      var desc = Object.getOwnPropertyDescriptor(proto, 'value');
      if (desc && typeof desc.set === 'function') {
        desc.set.call(input, value);
      } else {
        input.value = value;
      }
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function submitShopifyFormDOM(shopifyForm) {
    var btn = shopifyForm.querySelector('button[type="submit"], input[type="submit"]');
    if (btn) { btn.click(); return; }
    if (typeof shopifyForm.requestSubmit === 'function') {
      shopifyForm.requestSubmit();
      return;
    }
    shopifyForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  /** Final fallback: submit to the native /contact endpoint.
   *  Submissions land in Admin → Contact form notifications / customer messages. */
  function submitViaContactEndpoint(customForm, fieldMap) {
    var fd = new FormData();
    fd.append('form_type', 'contact');
    fd.append('utf8', '✓');
    Object.keys(fieldMap).forEach(function (key) {
      var src = customForm.querySelector(fieldMap[key]);
      if (src && src.value) fd.append('contact[' + key + ']', src.value);
    });
    log('Fallback: POST /contact', Object.fromEntries ? Object.fromEntries(fd) : '(formdata)');
    return fetch('/contact', {
      method: 'POST',
      body: fd,
      credentials: 'same-origin'
    }).then(function (res) {
      log('/contact response:', res.status, res.url, 'redirected:', res.redirected);
      return res.ok || res.redirected;
    });
  }

  function showFeedback(customForm, type, message) {
    var host = customForm.parentElement || customForm;
    var fb = host.querySelector(':scope > .influencer-form__feedback');
    if (!fb) {
      fb = document.createElement('p');
      fb.className = 'influencer-form__feedback';
      customForm.insertAdjacentElement('afterend', fb);
    }
    fb.classList.remove('is-success', 'is-error');
    fb.classList.add(type === 'success' ? 'is-success' : 'is-error');
    fb.setAttribute('role', type === 'success' ? 'status' : 'alert');
    fb.textContent = message;
  }

  /**
   * @param {HTMLFormElement} customForm - the form we control
   * @param {Object} fieldMap - { key: selector } pairs mapping custom-form inputs to Shopify Form keys
   * @param {string} successMessage
   * @param {Object} [staticValues] - fixed values to write into Shopify Form fields when the
   *                                  custom form doesn't have an equivalent input. Useful for
   *                                  short forms (e.g. the hero email-only form) because the
   *                                  Shopify Forms API returns 422 when optional fields arrive
   *                                  empty.
   */
  function wireCustomForm(customForm, fieldMap, successMessage, staticValues) {
    customForm.addEventListener('submit', function (e) {
      e.preventDefault();
      log('Custom form submitted. fieldMap:', fieldMap);
      dumpEnvironment();

      var submitBtn = customForm.querySelector('button[type="submit"], input[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;

      var reEnable = function () {
        setTimeout(function () { if (submitBtn) submitBtn.disabled = false; }, 1200);
      };

      var fallback = function (reason) {
        warn('Falling back to /contact. Reason:', reason);
        if (!FALLBACK_TO_CONTACT_ENDPOINT) {
          showFeedback(customForm, 'error', 'Submission backend unavailable. Please try again soon.');
          reEnable();
          return;
        }
        submitViaContactEndpoint(customForm, fieldMap).then(function (ok) {
          if (ok) {
            showFeedback(customForm, 'success', successMessage);
            customForm.reset();
          } else {
            showFeedback(customForm, 'error', 'Submission failed. Please try again in a moment.');
          }
          reEnable();
        }).catch(function (err) {
          warn('Contact fallback error:', err);
          showFeedback(customForm, 'error', 'Submission failed. Please try again in a moment.');
          reEnable();
        });
      };

      waitForShopifyForm(FORM_WAIT_MS).then(function (shopifyForm) {
        if (!shopifyForm) {
          return fallback('Shopify Form not found after ' + FORM_WAIT_MS + 'ms');
        }

        var inputsInShopifyForm = listVisibleInputs(shopifyForm);
        log('Shopify Form visible inputs:', inspectShopifyFormInputs(shopifyForm));

        var anyFilled = false;
        Object.keys(fieldMap).forEach(function (key) {
          var src = customForm.querySelector(fieldMap[key]);
          if (!src || !src.value) return;
          var dst = findFieldInShopifyForm(shopifyForm, key, inputsInShopifyForm);
          if (!dst) { warn('No target field for:', key); return; }
          setFieldValue(dst, src.value);
          anyFilled = true;
          log('Filled:', key, '→', dst);
        });

        // Fill any leftover required fields with static defaults so the
        // Shopify Forms API doesn't return 422 (happens on short forms where
        // the user only provides a subset of the fields).
        // Each value can be a string OR a function receiving the custom form
        // (useful to derive one field from another, e.g. name = email).
        if (staticValues) {
          Object.keys(staticValues).forEach(function (key) {
            if (key in fieldMap) {
              var alreadySrc = customForm.querySelector(fieldMap[key]);
              if (alreadySrc && alreadySrc.value) return;
            }
            var dst = findFieldInShopifyForm(shopifyForm, key, inputsInShopifyForm);
            if (!dst) return;
            if (dst.value) return;
            var raw = staticValues[key];
            var val = typeof raw === 'function' ? raw(customForm) : raw;
            if (val == null || val === '') return;
            setFieldValue(dst, val);
            log('Filled (static):', key, '→', dst);
          });
        }

        if (!anyFilled) {
          warn('No custom fields matched Shopify Form inputs.');
          warn('Inputs available in Shopify Form (use to tune FIELD_KEYWORDS):', inspectShopifyFormInputs(shopifyForm));
          showFeedback(customForm, 'error', 'Submission mapping failed. Please open the browser console and share the [influencer-forms] log.');
          reEnable();
          return;
        }

        submitShopifyFormDOM(shopifyForm);
        showFeedback(customForm, 'success', successMessage);
        customForm.reset();
        reEnable();
      });
    });
  }

  function init() {
    log('Bridge initialising…');
    dumpEnvironment();

    var mainForm = document.querySelector('.influencer-form__form');
    if (mainForm && !mainForm.dataset.wpWired) {
      mainForm.dataset.wpWired = '1';
      wireCustomForm(
        mainForm,
        {
          name: 'input[name="contact[name]"]',
          email: 'input[name="contact[email]"]',
          instagram: 'input[name="contact[instagram]"]',
          tiktok: 'input[name="contact[tiktok]"]'
        },
        "Thanks! Your application has been submitted — we'll be in touch soon."
      );
      log('Wired main form.');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
